import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Badge, Grid, Heading, SectionTabs, Stack, Text } from '@shared/ui';

/**
 * Course Catalog Page
 *
 * Route: /:locale/courses
 * Public page showing all available courses
 */
export const Route = createFileRoute('/$locale/courses/')({
  component: CourseCatalog,
});

function CourseCatalog() {
  const { locale } = Route.useParams();
  const { t } = useTranslation();

  // Placeholder courses data - using translation keys with literal types
  const courses = [
    {
      id: 'wave-1',
      titleKey: 'courses.wave1.title' as const,
      descriptionKey: 'courses.wave1.description' as const,
      levelKey: 'courses.levels.beginner' as const,
      duration: '3 days',
    },
    {
      id: 'wave-2',
      titleKey: 'courses.wave2.title' as const,
      descriptionKey: 'courses.wave2.description' as const,
      levelKey: 'courses.levels.intermediate' as const,
      duration: '4 days',
    },
    {
      id: 'wave-3',
      titleKey: 'courses.wave3.title' as const,
      descriptionKey: 'courses.wave3.description' as const,
      levelKey: 'courses.levels.advanced' as const,
      duration: '5 days',
    },
  ];

  const futureTabs = [
    { id: 'offline', label: t('courses.tabs.offline', 'Offline') },
    { id: 'online', label: t('courses.tabs.online', 'Online') },
  ];

  return (
    <>
      <title>{`${t('courses.catalog')} | ${t('metadata.title')}`}</title>
      <meta name="description" content={t('courses.catalogMeta')} />

      <main className="min-h-screen bg-background p-6">
        <Stack gap="lg">
          <SectionTabs items={futureTabs} activeId={futureTabs[0]?.id} />

          <Grid cols={3} gap="lg" className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <Link
                key={course.id}
                to="/$locale/courses/$courseId"
                params={{ locale, courseId: course.id }}
                className="group rounded-lg border border-border bg-card p-6 shadow-sm transition-all hover:border-primary hover:shadow-md"
              >
                <div className="mb-4 aspect-video rounded-md bg-muted" />

                <div className="mb-2 flex items-center gap-2">
                  <Badge>{t(course.levelKey)}</Badge>
                  <span className="text-xs text-muted-foreground">{course.duration}</span>
                </div>

                <Heading
                  as="h3"
                  size="lg"
                  className="mb-2 text-card-foreground group-hover:text-primary"
                >
                  {t(course.titleKey)}
                </Heading>
                <Text size="sm" color="muted">
                  {t(course.descriptionKey)}
                </Text>

                <Text className="mt-4 text-sm font-medium text-primary group-hover:underline">
                  {t('courses.learnMore')}
                </Text>
              </Link>
            ))}
          </Grid>

          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
            <Text color="muted">{t('courses.moreComingSoon')}</Text>
          </div>
        </Stack>
      </main>
    </>
  );
}
